/**
 * Assembled capability catalog. This is the single code-defined source of the
 * platform taxonomy + dependency graph. Only per-tenant *state* (overrides,
 * edition grants, audit) lives in the database — see the CMP migration.
 *
 * Mirrors `lib/ai/capabilities/index.ts` (an `as const` object) and
 * `lib/ai/registry.ts` (`keyof` typed accessors), so `useCapability(key)` gets
 * autocomplete and compile-time key validation exactly like `AiCapabilityKey`.
 */
import type { AnyCatalogNode } from '@/lib/capabilities/contracts';
import { modules } from './modules';
import { journeys } from './journeys';
import { capabilities } from './capabilities';
import { platformServices } from './platformServices';

/**
 * Order matters only for readability; keys are unique across layers by the
 * `<layer>.` prefix convention.
 */
export const CATALOG = {
  ...platformServices,
  ...capabilities,
  ...journeys,
  ...modules,
} as const;

export type CapabilityCatalog = typeof CATALOG;
/** Typed union of every catalog key — the ergonomic core of `useCapability`. */
export type CapabilityNodeKey = keyof typeof CATALOG;

/**
 * Bumped in code whenever the catalog's shape or dependency graph changes. It
 * is stamped into cache keys (busting the client cache on deploy) and into
 * audit/registry snapshots. Not a semver — a monotonic build tag.
 */
export const CATALOG_VERSION = '2026.10.31+1';

export function getNode<K extends CapabilityNodeKey>(key: K): CapabilityCatalog[K] {
  return CATALOG[key];
}

export function listNodes(): AnyCatalogNode[] {
  return Object.values(CATALOG) as AnyCatalogNode[];
}

export function isCapabilityNodeKey(key: string): key is CapabilityNodeKey {
  return Object.prototype.hasOwnProperty.call(CATALOG, key);
}

export { modules, journeys, capabilities, platformServices };
